import { API_CONFIG } from './config';

/**
 * Portal Auth API — recipient-facing authentication.
 *
 * Uses the same NestJS backend but separate JWT namespace (portal).
 * Calls go directly to the backend (no Next.js API proxy needed),
 * matching the existing lib/api/auth.ts pattern.
 */

const PORTAL_TOKEN_KEY = 'hf-portal:token';
const PORTAL_RECIPIENT_KEY = 'hf-portal:recipient';
const PORTAL_REFRESH_TOKEN_KEY = 'hf-portal:refresh-token';
const PORTAL_ADMIN_PREVIEW_KEY = 'hf-portal:admin-preview';
const PORTAL_SESSION_IDENTITY_KEY = 'hf-portal:session-identity';

/** Shared across tabs, and stable across rotation within the same login. */
function portalSessionIdentity(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(PORTAL_SESSION_IDENTITY_KEY);
}

function isCurrentPortalSession(identity: string | null): boolean {
  return portalSessionIdentity() === identity;
}

function replacePortalSessionIdentity(): void {
  const identity = Array.from(crypto.getRandomValues(new Uint32Array(4))).join('-');
  // Invalidate pending operations before replacing any of their credentials.
  localStorage.setItem(PORTAL_SESSION_IDENTITY_KEY, identity);
}

/** localStorage events reach other tabs; this event also updates this tab's
 * persistent layout after login, renewal and sign-out. */
export const PORTAL_SESSION_EVENT = 'hf-portal:session-changed';
function notifyPortalSession(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(PORTAL_SESSION_EVENT));
}

/**
 * The refresh currently in flight, and the raw token it is spending.
 *
 * SINGLE-FLIGHT, because portal rotation is now SINGLE-WINNER server-side
 * (`UPDATE … WHERE id = $1 AND revoked_at IS NULL RETURNING id`, and the
 * service refuses to issue when no row comes back). One token must not produce
 * two live 30-day chains — but the losers of a race now get a hard 401 where
 * they used to get a second chain, and the portal races on EVERY access-token
 * expiry: three independent call sites each invoke `refresh()` on their own
 * 401 (`authedFetch`, `use-portal-training.ts`, `use-portal-certificates.ts`)
 * and a page that fires training + certificates in parallel POSTs the same raw
 * token two or three times. Without this, the learner is signed out mid SCORM
 * attempt with no password to get back in.
 *
 * `lib/api/client.ts` has kept exactly this guard for the admin app since it
 * was written; the portal was simply never given one.
 */
let portalRefreshInFlight: Promise<string | null> | null = null;
let portalRefreshTokenInFlight: string | null = null;
let portalRefreshIdentityInFlight: string | null | undefined;

/**
 * Where the "Exit preview" control in the portal shell sends the admin back to.
 * The only caller of `openPortalAsRecipient` today is the recipients list.
 */
export const PORTAL_PREVIEW_RETURN_PATH = '/recipients';

export interface PortalRecipient {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  workspaceId: string;
  companyId: string;
  domainId: string | null;
  /**
   * Whether this recipient manages at least one other recipient (some other
   * `recipients` row has `manager_id = this.id`, scoped to the same workspace,
   * not soft-deleted). Computed server-side at JWT issuance.
   *
   * Used by the User Portal to decide whether to render the Manager View
   * toggle. Defaults to `false` on legacy login responses issued before this
   * field was added — the toggle stays hidden until the user re-logs.
   */
  isManager?: boolean;
  /**
   * The learner's language as one of the interface languages ('ar'), from the
   * directory, a CSV or a form; null when the portal has no catalogue for it.
   * The portal opens in it on a device where no language was chosen yet
   * (locale audit 2026-09-26, L-02). Absent from a login made before the API
   * sent it.
   */
  locale?: string | null;
}

/**
 * Why the API refused a portal one-time code — the `errorCode` it stamps on
 * the refusal (apps/api/src/auth/portal/portal-auth.service.ts,
 * PORTAL_OTP_ERROR_CODES). The login page words each one in the learner's
 * language instead of showing the API's English message
 * (lib/user-portal/sign-in-refusal.ts). Kept equal to the API's list by
 * app/(portal)/portal/login/__tests__/refusal-codes-match-the-api.guard.spec.ts.
 */
export const PORTAL_OTP_ERROR = {
  invalid: 'portal_otp_invalid',
  expired: 'portal_otp_expired',
  rateLimited: 'portal_otp_rate_limited',
  /** The code was right, but the learner can no longer sign in (removed, inactive, no portal access). */
  accountInactive: 'portal_account_inactive',
} as const;

/**
 * Why the API refused a portal magic link (PORTAL_MAGIC_LINK_ERROR_CODES in
 * apps/api/src/auth/portal/portal-auth.service.ts). A right link for a learner
 * who can no longer sign in answers PORTAL_OTP_ERROR.accountInactive. Kept
 * equal to the API's list by the same guard as PORTAL_OTP_ERROR.
 */
export const PORTAL_MAGIC_LINK_ERROR = {
  /**
   * The link cannot be used: none on file (already used, or past its 15
   * minutes), or not the one on file (a newer link replaced it, or it was
   * altered). One answer, so a guess never learns whether a link was requested.
   */
  invalid: 'portal_magic_link_invalid',
  /** The real link, but past its expiry. */
  expired: 'portal_magic_link_expired',
  rateLimited: 'portal_magic_link_rate_limited',
} as const;

/**
 * Why the API refused a portal single sign-on (PORTAL_SSO_ERROR_CODES in
 * apps/api/src/auth/portal/portal-auth.service.ts). Kept equal to the API's
 * list by the same guard as PORTAL_OTP_ERROR.
 */
export const PORTAL_SSO_ERROR = {
  notConfigured: 'portal_sso_not_configured',
  /** The sign-in attempt is unknown, used or past its 10 minutes. */
  expired: 'portal_sso_expired',
  /** The provider did not confirm who the learner is. */
  failed: 'portal_sso_failed',
  noAccount: 'portal_sso_no_account',
} as const;

/**
 * The learner's organisation is switched off (Suspended, Cancelled or
 * deleted): the `errorCode` of apps/api/src/auth/company-enabled.ts, which
 * every portal sign-in answers with once the code has matched.
 */
export const COMPANY_DISABLED_ERROR_CODE = 'company_disabled';

/**
 * A refused portal-auth call: the API's message (English, for logs), the HTTP
 * status and the API's `errorCode` when it sent one. Show the learner a
 * sentence chosen from `code` / `status`, never `message`.
 */
export class PortalAuthError extends Error {
  readonly status: number;
  readonly code: string | null;

  constructor(message: string, status: number, code: string | null) {
    super(message);
    // `extends Error` + a downlevelled target can break `instanceof`.
    Object.setPrototypeOf(this, PortalAuthError.prototype);
    this.name = 'PortalAuthError';
    this.status = status;
    this.code = code;
  }
}

/**
 * The PortalAuthError for a refused portal-auth response; `fallback` when the
 * body says nothing. Both portal sign-in clients throw it — `portalAuthApi`
 * below (OTP, magic link) and `portalSsoApi` in
 * components/portal/hooks/use-portal-sso.ts (OIDC, SAML, refresh) — so a page
 * can word the refusal from its code (lib/user-portal/sign-in-refusal.ts).
 */
export async function portalAuthError(res: Response, fallback: string): Promise<PortalAuthError> {
  const body = (await res.json().catch(() => ({}))) as { message?: unknown; errorCode?: unknown };
  const message = Array.isArray(body.message)
    ? body.message.join('; ')
    : typeof body.message === 'string' && body.message
      ? body.message
      : fallback;
  const code = typeof body.errorCode === 'string' ? body.errorCode : null;
  return new PortalAuthError(message, res.status, code);
}

/**
 * Marker written alongside the portal session when an ADMIN primed it via
 * "View as in Portal" (`openPortalAsRecipient`). A recipient who logs in
 * normally never has one, so its presence is what the portal shell keys the
 * impersonation banner + exit control off (#417).
 */
export interface PortalAdminPreview {
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
}

export interface PortalLoginResponse {
  accessToken: string;
  recipient: PortalRecipient;
}

export interface PortalSessionInput extends PortalLoginResponse {
  refreshToken?: string | null;
  adminPreview?: PortalAdminPreview | null;
}

export interface PortalWorkspaceInfo {
  workspaceId: string;
  workspaceName: string;
  companyName: string;
  domainName: string | null;
}

function portalUrl(path: string): string {
  return `${API_CONFIG.baseUrl}${API_CONFIG.versionPrefix}/portal/auth${path}`;
}

export const portalAuthApi = {
  /**
   * POST /v1/portal/auth/send-otp
   * Sends a 6-digit OTP to the recipient's email.
   * Always returns 200 (anti-enumeration).
   */
  async sendOtp(email: string): Promise<{ message: string }> {
    const res = await fetch(portalUrl('/send-otp'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });

    // A PortalAuthError: the page words its code/status, not the English message.
    if (!res.ok) throw await portalAuthError(res, 'Failed to send OTP');

    return res.json();
  },

  /**
   * POST /v1/portal/auth/verify-otp
   * Verifies the OTP and returns a portal JWT + recipient info.
   */
  async verifyOtp(email: string, otp: string): Promise<PortalLoginResponse> {
    const res = await fetch(portalUrl('/verify-otp'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, otp }),
    });

    // A PortalAuthError: the page words its code/status, not the English message.
    if (!res.ok) throw await portalAuthError(res, 'Invalid or expired OTP');

    return res.json();
  },

  /**
   * POST /v1/portal/auth/send-magic-link
   * Sends a magic link to the recipient's email.
   *
   * `workspaceId` is optional but STRONGLY RECOMMENDED — when supplied,
   * the BE scopes the lookup + Redis key by (workspaceId, email) instead
   * of email-only. This closes the MSP-scenario ambiguity where the
   * same email belongs to multiple workspaces and the BE would otherwise
   * silently pick one. The verify endpoint also matches the same scoped
   * Redis key shape via `?ws=` in the magic-link URL. Added 2026-05-14.
   */
  async sendMagicLink(
    email: string,
    workspaceId?: string,
  ): Promise<{ message: string }> {
    const res = await fetch(portalUrl('/send-magic-link'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, workspaceId }),
    });

    // A PortalAuthError: the page words its code/status, not the English message.
    if (!res.ok) throw await portalAuthError(res, 'Failed to send magic link');

    return res.json();
  },

  /**
   * GET /v1/portal/auth/verify?token=<token>&email=<email>&ws=<workspaceId>
   * Verify a magic link token and return a portal JWT + recipient info.
   *
   * The `ws` param is forwarded from the URL the recipient clicked (the BE
   * embeds it into the email's magic-link URL when the workspace was known
   * at send time). When present, the BE uses the workspace-scoped Redis
   * key. When absent (Safe Links stripped the query), the BE finds the
   * workspace from the token itself, then falls back to the legacy
   * email-only key — so `ws` is never needed to redeem (audit P6).
   */
  async verifyMagicLink(
    token: string,
    email: string,
    workspaceId?: string,
  ): Promise<PortalLoginResponse> {
    const qs = new URLSearchParams({ token, email });
    if (workspaceId) qs.set('ws', workspaceId);
    const res = await fetch(portalUrl(`/verify?${qs.toString()}`), {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    });

    // A PortalAuthError: the page words its code/status, not the English message.
    if (!res.ok) throw await portalAuthError(res, 'Invalid or expired magic link');

    return res.json();
  },

  /**
   * GET /v1/portal/auth/workspaces?email=<email>
   * Returns workspaces the recipient belongs to (multi-workspace selection).
   */
  async getWorkspaces(email: string): Promise<PortalWorkspaceInfo[]> {
    const res = await fetch(
      portalUrl(`/workspaces?email=${encodeURIComponent(email)}`),
    );

    if (!res.ok) {
      return [];
    }

    return res.json();
  },

  // ── Token helpers ─────────────────────────────────────────

  /** Replace the whole login, including credentials absent from its response.
   * OTP/magic links have no refresh token and must not inherit one from SSO. */
  setSession(session: PortalSessionInput): void {
    if (typeof window === 'undefined') return;
    replacePortalSessionIdentity();
    portalRefreshInFlight = null;
    portalRefreshTokenInFlight = null;
    portalRefreshIdentityInFlight = undefined;
    // ORDER MATTERS. Another portal tab hears these writes one storage event
    // per key and reads storage as it stands at each (useLearnerLanguage,
    // AdminPreviewBanner). A recipient beside the wrong "View as" marker reads
    // as that person signed in, and the language hook then saves an employee's
    // language as the admin's own. Every order that swaps the marker and the
    // recipient key-by-key passes through such a moment (one view-as to
    // another always does), so the old recipient goes FIRST: in between, that
    // tab sees no one signed in, then the new session whole.
    localStorage.removeItem(PORTAL_RECIPIENT_KEY);
    if (session.adminPreview) localStorage.setItem(PORTAL_ADMIN_PREVIEW_KEY, JSON.stringify(session.adminPreview));
    else localStorage.removeItem(PORTAL_ADMIN_PREVIEW_KEY);
    localStorage.setItem(PORTAL_TOKEN_KEY, session.accessToken);
    localStorage.setItem(PORTAL_RECIPIENT_KEY, JSON.stringify(session.recipient));
    if (session.refreshToken) localStorage.setItem(PORTAL_REFRESH_TOKEN_KEY, session.refreshToken);
    else localStorage.removeItem(PORTAL_REFRESH_TOKEN_KEY);
    notifyPortalSession();
  },

  getToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(PORTAL_TOKEN_KEY);
  },

  setToken(token: string): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PORTAL_TOKEN_KEY, token);
    notifyPortalSession();
  },

  getRecipient(): PortalRecipient | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(PORTAL_RECIPIENT_KEY);
    if (!raw) return null;
    try {
      return JSON.parse(raw) as PortalRecipient;
    } catch {
      return null;
    }
  },

  setRecipient(recipient: PortalRecipient): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PORTAL_RECIPIENT_KEY, JSON.stringify(recipient));
    notifyPortalSession();
  },

  /**
   * Prime the portal session with an externally-issued token pair (e.g. the
   * admin `direct-login` response) and open the employee portal in a new tab.
   *
   * Because the admin app and portal share the same origin, writing to
   * `localStorage` here is visible to the portal tab on mount — the portal
   * layout reads `hf-portal:token` via `getToken()` and proceeds as if the
   * recipient logged in normally.
   *
   * The session is additionally flagged as an ADMIN PREVIEW so the portal
   * shell can say whose account is on screen and offer a way out (#417) —
   * without it the portal renders as an indistinguishable genuine session.
   *
   * @returns the opened window handle (may be null if the popup blocker fires).
   */
  openPortalAsRecipient(params: {
    accessToken: string;
    refreshToken?: string | null;
    recipient: PortalRecipient;
  }): Window | null {
    if (typeof window === 'undefined') return null;
    // Persist session state FIRST so the portal tab reads a fully-populated
    // localStorage on mount (the `load` handler doesn't re-poll localStorage).
    this.setSession({
      ...params,
      adminPreview: {
        recipientId: params.recipient.id,
        recipientName: [params.recipient.firstName, params.recipient.lastName]
          .filter(Boolean)
          .join(' '),
        recipientEmail: params.recipient.email,
      },
    });

    const ws = encodeURIComponent(params.recipient.workspaceId);
    // #417 — the access token is NOT put on the URL. A bearer token in a
    // query string leaks into browser history, Referer headers and every
    // proxy/access log; the portal reads the session from localStorage on
    // the same origin, so the copy was redundant as well as unsafe.
    const url = `/portal?ws=${ws}`;
    return window.open(url, '_blank', 'noopener');
  },

  // ── Admin preview marker (#417) ──────────────────────────────────

  getAdminPreview(): PortalAdminPreview | null {
    if (typeof window === 'undefined') return null;
    const raw = localStorage.getItem(PORTAL_ADMIN_PREVIEW_KEY);
    if (!raw) return null;
    let preview: PortalAdminPreview;
    try {
      preview = JSON.parse(raw) as PortalAdminPreview;
    } catch {
      return null;
    }
    // A preview tab closed without pressing "Exit preview" strands the marker
    // in shared localStorage. Only honour it while it still describes the
    // session actually stored — otherwise a later genuine login on the same
    // machine would be captioned with the previewed employee's name.
    return preview.recipientId === this.getRecipient()?.id ? preview : null;
  },

  setAdminPreview(preview: PortalAdminPreview): void {
    if (typeof window === 'undefined') return;
    localStorage.setItem(PORTAL_ADMIN_PREVIEW_KEY, JSON.stringify(preview));
    notifyPortalSession();
  },

  isAdminPreview(): boolean {
    return this.getAdminPreview() !== null;
  },

  // ── Refresh token (Phase 5) ──────────────────────────────────────

  /**
   * Phase 5 — raw refresh token returned alongside the access token on
   * SSO / SAML / direct-login. Stored in localStorage (same surface as
   * the access token) for now. When the portal moves behind a secure
   * cookie domain, swap this for `document.cookie` with `HttpOnly` +
   * `Secure` via a small BE endpoint; the hook surface stays the same.
   */
  getRefreshToken(): string | null {
    if (typeof window === 'undefined') return null;
    return localStorage.getItem(PORTAL_REFRESH_TOKEN_KEY);
  },

  setRefreshToken(token: string | null): void {
    if (typeof window === 'undefined') return;
    if (!token) {
      localStorage.removeItem(PORTAL_REFRESH_TOKEN_KEY);
      notifyPortalSession();
      return;
    }
    localStorage.setItem(PORTAL_REFRESH_TOKEN_KEY, token);
    notifyPortalSession();
  },

  /**
   * Call `/v1/portal/auth/refresh` with the stored refresh token and
   * persist the rotated pair. Returns the new access token on success
   * or `null` on any failure (caller falls back to login redirect).
   *
   * SINGLE-FLIGHT — see `portalRefreshInFlight` above. Concurrent callers
   * spending the SAME raw token share one request and one answer, so only one
   * of them reaches the server's single-winner rotation.
   *
   * CROSS-TAB RECOVERY. In-process de-duplication cannot cover two open portal
   * tabs: they are separate processes sharing one localStorage. So when the
   * call fails, this re-reads storage before answering — if the stored refresh
   * token is no longer the one we spent, another tab won the rotation and its
   * access token is live. Returning that beats returning `null`, which sends a
   * learner with a perfectly good session to the login screen.
   */
  async refresh(identity = portalSessionIdentity()): Promise<string | null> {
    const refreshToken = this.getRefreshToken();
    if (!refreshToken || !isCurrentPortalSession(identity)) return null;

    // Join the flight already spending this exact token. A flight spending a
    // DIFFERENT token is stale and must not be joined — the caller's token is
    // the newer one.
    if (portalRefreshInFlight && portalRefreshTokenInFlight === refreshToken) {
      if (portalRefreshIdentityInFlight === identity) return portalRefreshInFlight;
    }

    portalRefreshTokenInFlight = refreshToken;
    portalRefreshIdentityInFlight = identity;
    portalRefreshInFlight = (async (): Promise<string | null> => {
      try {
        const res = await fetch(portalUrl('/refresh'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });
        if (!isCurrentPortalSession(identity)) return null;
        if (!res.ok) return this.recoverRotationLoser(refreshToken, identity);

        const body = (await res.json()) as {
          accessToken?: string;
          refreshToken?: string;
        };
        if (!isCurrentPortalSession(identity)) return null;
        if (!body.accessToken || !body.refreshToken) {
          return this.recoverRotationLoser(refreshToken, identity);
        }

        this.setToken(body.accessToken);
        this.setRefreshToken(body.refreshToken);
        return body.accessToken;
      } catch {
        if (!isCurrentPortalSession(identity)) return null;
        return this.recoverRotationLoser(refreshToken, identity);
      } finally {
        if (portalRefreshIdentityInFlight === identity && portalRefreshTokenInFlight === refreshToken) {
          portalRefreshInFlight = null;
          portalRefreshTokenInFlight = null;
          portalRefreshIdentityInFlight = undefined;
        }
      }
    })();

    return portalRefreshInFlight;
  },

  /**
   * A refresh failed. Did another tab already rotate past us?
   *
   * Only answers with a token when storage has genuinely MOVED ON from the one
   * we spent — otherwise it returns null and the caller redirects to login, so
   * a real expiry is still a real expiry. It never writes: the winner's tokens
   * are already in storage and clobbering them is what this exists to avoid.
   */
  recoverRotationLoser(spentRefreshToken: string, identity = portalSessionIdentity()): string | null {
    if (!isCurrentPortalSession(identity)) return null;
    const current = this.getRefreshToken();
    if (!current || current === spentRefreshToken) return null;
    const token = this.getToken();
    return isCurrentPortalSession(identity) ? token : null;
  },

  /**
   * POST /v1/portal/auth/logout — end the session server-side, then drop the
   * local tokens.
   *
   * Until this existed the portal had no logout at all: `clearSession()` cleared
   * localStorage and the 30-day refresh chain kept running on the server, so
   * signing out on a shared machine revoked nothing.
   *
   * ALWAYS clears locally, even when the network call fails — a learner who
   * pressed Sign out must not be left holding tokens because the request timed
   * out. The server-side chain revocation is what the call buys; losing it falls
   * back to today's behaviour rather than to a broken button.
   *
   * THE ACCESS TOKEN DIES TOO, since 2026-09-21. It carries a `sid` (its
   * `portal_sessions` row) that the portal JWT strategy re-reads on every
   * request, and this call revokes that row — so a copy already taken off this
   * machine stops working instead of surviving its remaining 8 hours. That is
   * why the call matters even when it looks like a formality.
   *
   * THAT NOW INCLUDES MAGIC-LINK AND OTP LOGINS — the common case. They still
   * issue NO refresh token (only SSO / SAML and admin direct-login do), so the
   * response carries `hadRefreshToken: false`; the session row is what gets
   * revoked, reported as `revokedSession: true`. The ONE residual is a token
   * minted before the claim existed: it has no sid, nothing to revoke, and lives
   * out its 8h — and the API refuses such tokens outright after 2026-09-24.
   */
  async logout(): Promise<void> {
    const identity = portalSessionIdentity();
    const refreshToken = this.getRefreshToken();
    const accessToken = this.getToken();
    try {
      if (accessToken || refreshToken) {
        // A dormant page may still hold a valid refresh chain after its access
        // bearer expires. Renew on 401 before revoking, while both credentials
        // remain available. The old refresh token in the body still identifies
        // the chain, including the successor issued by that renewal.
        await this.authedFetch(portalUrl('/logout'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          // Omitted, not empty: a magic-link / OTP session HAS no refresh
          // token, and an empty string is a client bug the API rejects.
          body: JSON.stringify(refreshToken ? { refreshToken } : {}),
        }, identity);
      }
    } catch {
      // Swallowed on purpose — see the doc comment.
    } finally {
      // A newer login owns storage now; finishing this older logout must not
      // erase it. Ordinary failures still clear the session that initiated it.
      if (isCurrentPortalSession(identity)) this.clearSession();
    }
  },

  clearSession(): void {
    if (typeof window === 'undefined') return;
    replacePortalSessionIdentity();
    portalRefreshInFlight = null;
    portalRefreshTokenInFlight = null;
    portalRefreshIdentityInFlight = undefined;
    localStorage.removeItem(PORTAL_TOKEN_KEY);
    localStorage.removeItem(PORTAL_RECIPIENT_KEY);
    localStorage.removeItem(PORTAL_REFRESH_TOKEN_KEY);
    localStorage.removeItem(PORTAL_ADMIN_PREVIEW_KEY);
    notifyPortalSession();
  },

  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) return false;
    // 2026-05-22 — also reject EXPIRED tokens, not just missing ones.
    // Pre-fix, an expired-but-present token returned `true`, the page
    // proceeded to fetch authenticated endpoints, and the user saw an
    // ugly "Course Not Available" error instead of being routed to
    // login. Decode the JWT payload (no signature check — that's the
    // BE's job) and compare `exp` (seconds since epoch) against now.
    // On any decode failure, fall through to the truthy-token answer so
    // a malformed token still triggers the login redirect via the
    // downstream `getToken()` consumer instead of silently locking the
    // user out.
    try {
      const parts = token.split('.');
      if (parts.length !== 3) return true;
      const payload = JSON.parse(
        // base64url → base64 → JSON
        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')),
      ) as { exp?: number };
      if (typeof payload.exp !== 'number') return true;
      // Add a 10s clock-skew buffer — treat tokens about to expire as
      // already expired so an in-flight request doesn't race the boundary.
      const nowSec = Math.floor(Date.now() / 1000);
      return payload.exp > nowSec + 10;
    } catch {
      return true;
    }
  },

  /**
   * Authenticated fetch helper with automatic 401 → refresh → retry.
   * Use this wherever portal hooks hit authenticated endpoints.
   */
  async authedFetch(
    input: RequestInfo | URL,
    init?: RequestInit,
    identity = portalSessionIdentity(),
  ): Promise<Response> {
    const headers = new Headers(init?.headers);
    const token = this.getToken();
    if (!isCurrentPortalSession(identity)) throw new DOMException('Portal session changed', 'AbortError');
    if (token) headers.set('Authorization', `Bearer ${token}`);

    const res = await fetch(input, { ...init, headers });
    if (res.status !== 401 || !isCurrentPortalSession(identity)) return res;

    const refreshed = await this.refresh(identity);
    if (!refreshed || !isCurrentPortalSession(identity)) return res;

    const retryHeaders = new Headers(init?.headers);
    retryHeaders.set('Authorization', `Bearer ${refreshed}`);
    return fetch(input, { ...init, headers: retryHeaders });
  },
};
